import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const PORTRAIT_IMG = "/images/breeza-quezada-headshot.jpg";
const PORTRAIT_2 = "/images/calm-lounge.jpg";

const About = () => {
  usePageMeta(
    "About Your Therapist — California LCSW | Holistic Healing Psychotherapy, Inc.",
    "Meet a California Licensed Clinical Social Worker with nearly 20 years of experience helping people through relationship struggles, trauma and major life transitions."
  );

  return (
    <div data-testid="about-page">
      <section className="bg-cream-50 pt-14 pb-16 lg:pt-24 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <FadeIn>
              <p className="eyebrow mb-6" data-testid="about-eyebrow">About Me</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-ink" data-testid="about-headline">
                Easy to talk to. <em className="text-gold-600">Clinically strategic.</em>{" "}
                Focused on real change.
              </h1>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div className="mt-8 space-y-5 text-base leading-relaxed text-ink/80 max-w-2xl" data-testid="about-bio">
                <p>
                  I'm Breeza Y. Quezada, a California Licensed Clinical Social Worker (LCSW #28692) with nearly 20
                  years of experience helping people navigate some of the most difficult seasons of their lives. I
                  earned my Master of Social Work from USC and my Bachelor of Arts in Psychology from UC Irvine. My
                  work has given me the opportunity to sit with people through relationship struggles, family
                  challenges, trauma, emotional distress and major life transitions — and I never lose sight of the
                  fact that every person who walks into therapy is more than a diagnosis or a problem to solve.
                </p>
                <p>
                  My career has included five years with the Los Angeles County Department of Mental Health, providing
                  clinical care to individuals facing mental-health, substance-use and socioeconomic challenges, and
                  more than a decade with a major healthcare organization providing individual, family, group, crisis
                  and couples therapy.
                </p>
                <p>
                  As a mother of three, I also understand that relationships exist within real life. Parenting, work,
                  family responsibilities, changing priorities and unexpected challenges can place enormous pressure
                  on even strong relationships. I bring that real-world perspective into my work while maintaining the
                  clinical training and boundaries that effective therapy requires.
                </p>
                <p>
                  My approach is warm, direct and nonjudgmental. I believe therapy should be a place where you can be
                  honest about what is happening without feeling shamed or blamed. My goal is not simply to help you
                  feel better for an hour; it is to help you understand the patterns keeping you stuck and develop
                  healthier ways of responding to one another and to life's challenges.
                </p>
                <p>
                  After nearly two decades in the mental-health field, I still believe one of the most meaningful
                  parts of this work is witnessing people discover that change is possible — even when they have begun
                  to lose hope.
                </p>
              </div>
              <Link
                to="/contact"
                data-testid="about-schedule-cta"
                className="inline-flex items-center gap-2 mt-9 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-forest-700"
              >
                Schedule a Consultation <ArrowRight size={16} />
              </Link>
            </FadeIn>
          </div>
          <div className="lg:col-span-5 space-y-8">
            <FadeIn delay={0.2} className="editorial-frame">
              <img
                src={PORTRAIT_IMG}
                alt="Breeza Y. Quezada, LCSW #28692"
                className="relative z-10 w-full h-[360px] sm:h-[480px] object-contain bg-cream-200"
                data-testid="about-portrait-image"
              />
            </FadeIn>
            <FadeIn delay={0.3}>
              <div className="bg-forest-900 text-cream-50 p-8" data-testid="about-credentials-card">
                <p className="eyebrow mb-5">Credentials</p>
                <ul className="space-y-3 text-sm text-cream-50/80">
                  <li className="flex gap-3"><span className="text-gold-400">◆</span> California Licensed Clinical Social Worker · LCSW #28692</li>
                  <li className="flex gap-3"><span className="text-gold-400">◆</span> Master of Social Work — University of Southern California, 2009</li>
                  <li className="flex gap-3"><span className="text-gold-400">◆</span> Bachelor of Arts in Psychology — UC Irvine, 2006</li>
                  <li className="flex gap-3"><span className="text-gold-400">◆</span> Nearly 20 years of clinical experience</li>
                  <li className="flex gap-3"><span className="text-gold-400">◆</span> Los Angeles County Department of Mental Health — 5 years</li>
                  <li className="flex gap-3"><span className="text-gold-400">◆</span> 12+ years with a major healthcare organization — individual, family, group, crisis & couples therapy</li>
                  <li className="flex gap-3"><span className="text-gold-400">◆</span> Trauma-informed, evidence-based practice</li>
                  <li className="flex gap-3"><span className="text-gold-400">◆</span> Bilingual — therapy in English & Spanish (se habla español)</li>
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-cream-100 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <FadeIn className="editorial-frame lg:sticky lg:top-28">
            <img
              src={PORTRAIT_2}
              alt="A calm, light-filled lounge with a couch and warm minimal furnishings"
              className="relative z-10 w-full h-[320px] sm:h-[420px] object-cover"
              data-testid="about-secondary-image"
            />
          </FadeIn>
          <div>
            <SectionHeading eyebrow="A Personal Note" title="In my own words." />
            <FadeIn delay={0.15}>
              <div className="space-y-5 text-base leading-relaxed text-ink/80 mt-2" data-testid="about-personal-note">
                <p>
                  First, I want to acknowledge that reaching out to a therapist is a big step — whether this is your
                  first time or you've done this before. Finding the right therapist takes a little work, but it is
                  worth it: the journey is so much richer when you walk it alongside someone you genuinely connect
                  with and trust. So let me share a little about myself, to help you decide whether working together
                  feels right for you.
                </p>
                <p>
                  In the nearly 20 years I've provided care, what I've heard most is that I'm easy to talk to. I bring
                  a sense of humor into the room. I can keep things light when you're having a hard time — and hold
                  you accountable to your goals when I see you need a gentle push. That careful, clinical
                  push-and-pull is what allows the people in my care to get what they need, when they need it.
                </p>
                <p>
                  Whether you want to break free from unhealthy beliefs or habits, a traumatic past, anxiety,
                  depression, a struggling marriage, challenging relationships, an unhealthy work environment — or
                  any other very human problem — the change, growth and understanding you're looking for is possible.
                </p>
                <p>
                  I lead with care, compassion and strategic clinical focus, and I am thoroughly prepared to help you
                  on your journey. If you're ready to take the next step toward newfound focus, vision, strength and
                  healing — I encourage you to reach out.
                </p>
                <p className="font-serif italic text-xl text-forest-700">— Breeza</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default About;
